import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Button,
  Box,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  getPaymentMethodAsync
} from "../infrastructure/httpclient";
import { useSnackbar } from 'notistack';
import { useAuth } from '../context/AuthContext';
import { createSubscriptionAsync } from '../infrastructure/httpclient';
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { availableWorkflows } from '../infrastructure/workflows';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// New component for the Stripe Pay button
const StripePayButton = ({ workflow, token, user, onPaymentSuccess, enqueueSnackbar, hasPaymentMethod }) => {
  const stripe = useStripe();
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handlePayClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
  };

  const processPayment = async () => {
    handleConfirmClose();
    if (!stripe) {
      enqueueSnackbar('Stripe has not loaded yet. Please try again.', { variant: 'warning' });
      return;
    }

    if (!hasPaymentMethod) {
      enqueueSnackbar('No saved payment method found. Please add a payment method first.', { variant: 'warning' });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        workflow_name: workflow.workflow_name,
        price_id: workflow.stripe_priceid,
      };

      const createSubResponse = await createSubscriptionAsync(token, payload);
      const clientSecret = createSubResponse.data.clientSecret;

      const { error } = await stripe.confirmCardPayment(clientSecret);

      if (error) {
        throw new Error(error.message);
      }

      enqueueSnackbar('Subscription added. Payment was successful!', { variant: 'success' });
      onPaymentSuccess();
    } catch (err) {
      enqueueSnackbar(err.message || 'Payment failed. Please try again.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={handlePayClick}
        disabled={loading || !stripe}
        sx={{ mt: 2 }}
      >
        {loading ? 'Processing...' : 'Pay with Stripe'}
      </Button>
      <Dialog
        open={confirmOpen}
        onClose={handleConfirmClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Confirm Payment"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you ready to pay now? (if the automation you are subscribing is recurring payments, remember to provide a card that has good buffer in the expiry date)
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose}>Cancel</Button>
          <Button onClick={processPayment} color="primary" autoFocus>
            Pay
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const SubscriptionPage = () => {
  const { user, token } = useAuth();
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const workflow = availableWorkflows.find((w) => w.id === workflowId);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (isSubscribed) {
      const timer = setInterval(() => {
        setCountdown((prevCountdown) => prevCountdown - 1);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isSubscribed]);

  useEffect(() => {
    if (countdown === 0) {
      navigate('/workflows', { state: { refresh: new Date().getTime() } });
    }
  }, [countdown, navigate]);

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const response = await getPaymentMethodAsync(token);
        if (response?.data) {
          setHasPaymentMethod(true);
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          enqueueSnackbar("Failed to fetch payment method.", { variant: "info" });
        }
      }
    };

    fetchCard();
  }, [enqueueSnackbar, token]);



  const handlePaymentSuccess = () => {
    setIsSubscribed(true);
  };

  if (!user) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography variant="h6">
          You must be logged in to view this page.
        </Typography>
      </Container>
    );
  }


  if (!workflow) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography variant="h6">Workflow not found.</Typography>
      </Container>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Subscribe to {workflow.name}
          </Typography>

          <Card sx={{ mt: 3 }}>
            <CardHeader title={workflow.name} />
            <CardContent>
              <Typography variant="body1" gutterBottom>{workflow.problem}</Typography>
              <div>
                {workflow.details.split('\n\n').map((paragraph, index) => {
                  if (index === 0) {
                    return <Typography variant="body2" color="text.secondary" key={index} sx={{ whiteSpace: 'pre-line' }} gutterBottom>{paragraph}</Typography>;
                  }

                  const parts = paragraph.split('\n');
                  const title = parts[0];
                  const description = parts.slice(1).join('\n');

                  return (
                    <div key={index}>
                      <Typography variant="h6" component="h3" sx={{ mt: 2, fontWeight: 'bold' }} gutterBottom>
                        {title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }} gutterBottom>
                        {description}
                      </Typography>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {isSubscribed ? (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" color="primary">
                  You have successfully subscribed to this workflow!
                </Typography>
                <Typography variant="body1">
                  Redirecting to workflows page in {countdown} seconds...
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ mt: 3 }}>
              <CardHeader title="Price" />
              <CardContent>
                <Box>
                  <Typography variant="body1">{workflow.price_text}</Typography>
                  <StripePayButton
                    workflow={workflow}
                    token={token}
                    user={user}
                    onPaymentSuccess={handlePaymentSuccess}
                    enqueueSnackbar={enqueueSnackbar}
                    hasPaymentMethod={hasPaymentMethod}
                  />
                </Box>
              </CardContent>
            </Card>
          )}
        </Paper>
      </Container>
    </Elements>
  );
};

export default SubscriptionPage;
